import type { ArcadeScreen } from "../ScreenManager.js";
import type { MenuAction } from "../MenuInput.js";
import { uiSfx } from "../uiSfx.js";

/**
 * MAIN MENU — vertical arcade list + player card (menu-redesign-plan §3.3).
 *
 * Entries are routed by main.ts: QUICK MATCH → queue flow, LOBBIES → hosted
 * lobby screens (M4), GARAGE/PRACTICE/RANKINGS/OPTIONS → first-class arcade
 * screens (M3).
 *
 * Navigation: up/down + confirm/back via handleInput (keyboard AND gamepad,
 * one code path); mouse hover focuses, click activates; touch just taps.
 * The focused entry always carries the visible focus ring.
 */

export type MainMenuEntryId =
  | "quickmatch"
  | "lobbies"
  | "garage"
  | "practice"
  | "rankings"
  | "options";

export interface MainMenuDeps {
  version: string;
  getAuth: () => { loggedIn: boolean; username: string | null; credits: number };
  getCallsign: () => string;
  getVehicle: () => { name: string; id: string; trim: string } | null;
  /** Non-blocking stats refresh; resolves null for guests / on failure. */
  refreshStats: () => Promise<{ credits: number; wins: number; losses: number } | null>;
  /** Route a selected entry. */
  onSelect: (id: MainMenuEntryId) => void;
  /** Footer "SIGN IN" chip → classic ACCOUNT tab. */
  onSignIn: () => void;
  /** Escape/B on the menu → back to TITLE. */
  onBack: () => void;
}

interface MenuEntry {
  id: MainMenuEntryId;
  label: string;
  sub: string;
}

const ENTRIES: readonly MenuEntry[] = [
  { id: "quickmatch", label: "QUICK MATCH", sub: "STRAIGHT INTO THE QUEUE — MOST KILLS WINS" },
  { id: "lobbies", label: "LOBBIES", sub: "HOST A ROOM · JOIN BY CODE" },
  { id: "garage", label: "GARAGE", sub: "VEHICLES · UPGRADES · AI BUILDER" },
  { id: "practice", label: "PRACTICE", sub: "DUMMY MATCH · TEST LAB · COMPARISON LAB" },
  { id: "rankings", label: "RANKINGS", sub: "GLOBAL LEADERBOARD · YOUR STATS" },
  { id: "options", label: "OPTIONS", sub: "CONTROLS · VIDEO · AUDIO · ACCOUNT" },
];

const NOTICE_MS = 6000;

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : c === '"' ? "&quot;" : "&#39;",
  );
}

export class MainMenuScreen implements ArcadeScreen {
  private root: HTMLElement | null = null;
  private entryEls: HTMLButtonElement[] = [];
  private focusIdx = 0;
  private active = false;
  private enterSeq = 0;
  private notice: string | null = null;
  private noticeTimer: ReturnType<typeof setTimeout> | null = null;

  // Card elements
  private callsignEl: HTMLElement | null = null;
  private statusEl: HTMLElement | null = null;
  private vNameEl: HTMLElement | null = null;
  private vClassEl: HTMLElement | null = null;
  private statsEl: HTMLElement | null = null;
  private chipEl: HTMLButtonElement | null = null;
  private noticeEl: HTMLElement | null = null;

  constructor(private readonly deps: MainMenuDeps) {}

  mount(root: HTMLElement): void {
    this.root = root;
    root.innerHTML = `
      <div class="mm-wrap">
        <div class="mm-head">
          <span class="mm-eyebrow">MAIN MENU</span>
          <span class="mm-headrule" aria-hidden="true"></span>
        </div>
        <nav class="mm-list" aria-label="Main menu"></nav>
        <aside class="mm-card">
          <div class="mm-card-eyebrow">PLAYER</div>
          <div class="mm-callsign"></div>
          <span class="mm-card-status"></span>
          <div class="mm-vehicle">
            <div class="mm-vehicle-eyebrow">CURRENT RIDE</div>
            <div class="mm-vehicle-name"></div>
            <div class="mm-vehicle-class"></div>
            <button type="button" class="mm-vehicle-hint">OPEN GARAGE ▸</button>
          </div>
          <div class="mm-stats"></div>
          <button type="button" class="mm-signin-chip hidden">SIGN IN FOR PROGRESSION ▸</button>
        </aside>
        <div class="mm-notice hidden"></div>
        <div class="mm-footer">
          <span class="mm-version">v${this.deps.version}</span>
          <span class="mm-hints">↑↓ SELECT &nbsp;·&nbsp; ENTER / Ⓐ CONFIRM &nbsp;·&nbsp; ESC / Ⓑ BACK</span>
        </div>
      </div>
      `;

    const list = root.querySelector(".mm-list")!;
    this.entryEls = [];
    ENTRIES.forEach((entry, i) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "mm-entry";
      btn.dataset["id"] = entry.id;
      btn.innerHTML = `<span class="mm-label">${entry.label}</span><span class="mm-sub">${entry.sub}</span>`;
      btn.addEventListener("mouseenter", () => this.focusTo(i, true));
      btn.addEventListener("focus", () => this.focusTo(i, true));
      btn.addEventListener("click", () => {
        this.focusTo(i, false);
        this.activate();
      });
      list.appendChild(btn);
      this.entryEls.push(btn);
    });

    this.callsignEl = root.querySelector(".mm-callsign");
    this.statusEl = root.querySelector(".mm-card-status");
    this.vNameEl = root.querySelector(".mm-vehicle-name");
    this.vClassEl = root.querySelector(".mm-vehicle-class");
    this.statsEl = root.querySelector(".mm-stats");
    this.chipEl = root.querySelector(".mm-signin-chip");
    this.noticeEl = root.querySelector(".mm-notice");

    root.querySelector(".mm-vehicle-hint")?.addEventListener("click", () => {
      uiSfx.play("confirm");
      this.deps.onSelect("garage");
    });
    this.chipEl?.addEventListener("click", () => {
      uiSfx.play("confirm");
      this.deps.onSignIn();
    });
  }

  onEnter(): void {
    this.active = true;
    this.applyFocus();
    this.renderCard();
    this.renderNotice();

    // Fresh credits / W-L for the card — non-blocking, stale-guarded.
    const seq = ++this.enterSeq;
    void this.deps.refreshStats().then((stats) => {
      if (stats && this.active && seq === this.enterSeq) {
        this.renderStatsRows(stats.credits, `${stats.wins} / ${stats.losses}`);
      }
    });
  }

  onLeave(): void {
    this.active = false;
  }

  handleInput(action: MenuAction): boolean {
    switch (action.type) {
      case "up":
        this.move(-1);
        return true;
      case "down":
        this.move(1);
        return true;
      case "confirm":
      case "start":
        this.activate();
        return true;
      case "back":
        uiSfx.play("back");
        this.deps.onBack();
        return true;
      default:
        return false;
    }
  }

  /** Transient error strip (e.g. quickmatch failure) shown above the footer. */
  setNotice(msg: string): void {
    this.notice = msg;
    if (this.noticeTimer !== null) clearTimeout(this.noticeTimer);
    this.noticeTimer = setTimeout(() => {
      this.notice = null;
      this.noticeTimer = null;
      this.renderNotice();
    }, NOTICE_MS);
    this.renderNotice();
  }

  // ── internals ───────────────────────────────────────────────────────────

  private move(delta: number): void {
    const next = (this.focusIdx + delta + ENTRIES.length) % ENTRIES.length;
    this.focusTo(next, true);
  }

  private focusTo(idx: number, sfx: boolean): void {
    if (idx === this.focusIdx) {
      this.applyFocus();
      return;
    }
    this.focusIdx = idx;
    this.applyFocus();
    if (sfx) uiSfx.play("nav");
  }

  private applyFocus(): void {
    this.entryEls.forEach((el, i) => {
      el.classList.toggle("is-focused", i === this.focusIdx);
      if (i === this.focusIdx) el.setAttribute("aria-current", "true");
      else el.removeAttribute("aria-current");
    });
  }

  private activate(): void {
    const entry = ENTRIES[this.focusIdx];
    if (!entry) return;
    uiSfx.play("confirm");
    this.deps.onSelect(entry.id);
  }

  private renderCard(): void {
    const auth = this.deps.getAuth();

    if (this.callsignEl) this.callsignEl.textContent = this.deps.getCallsign().toUpperCase();
    if (this.statusEl) {
      this.statusEl.textContent = auth.loggedIn ? "SIGNED IN" : "GUEST";
      this.statusEl.classList.toggle("is-guest", !auth.loggedIn);
    }

    const vehicle = this.deps.getVehicle();
    if (this.vNameEl) {
      this.vNameEl.textContent = vehicle ? vehicle.name.toUpperCase() : "—";
      if (vehicle) {
        this.vNameEl.style.color = vehicle.trim;
        this.vNameEl.style.textShadow = `0 0 12px ${vehicle.trim}`;
      } else {
        this.vNameEl.style.color = "";
        this.vNameEl.style.textShadow = "";
      }
    }
    if (this.vClassEl) {
      this.vClassEl.textContent = vehicle ? vehicle.id.replace(/-/g, " ").toUpperCase() : "";
    }

    if (auth.loggedIn) {
      this.renderStatsRows(auth.credits, "…");
      this.chipEl?.classList.add("hidden");
    } else {
      if (this.statsEl) {
        this.statsEl.innerHTML =
          `<div class="mm-stat"><span>PROGRESSION</span><b>GUEST — NOT SAVED</b></div>`;
      }
      this.chipEl?.classList.remove("hidden");
    }
  }

  private renderStatsRows(credits: number, winLoss: string): void {
    if (!this.statsEl) return;
    this.statsEl.innerHTML =
      `<div class="mm-stat"><span>CREDITS</span><b class="mm-stat-credits">✦ ${credits}</b></div>` +
      `<div class="mm-stat"><span>W / L</span><b>${escapeHtml(winLoss)}</b></div>`;
  }

  private renderNotice(): void {
    if (!this.noticeEl) return;
    if (this.notice) {
      this.noticeEl.textContent = this.notice;
      this.noticeEl.classList.remove("hidden");
    } else {
      this.noticeEl.textContent = "";
      this.noticeEl.classList.add("hidden");
    }
  }
}
